import {
  IHealthScribeController,
  IHealthScribeService,
  IHealthScribeWebSocketProvider,
  IHealthScribeAudioProvider,
  IStartRecordingRequest,
  IStartRecordingResponse,
  IStopRecordingRequest,
  ITranscriptionMessage,
  IListTranscriptionsRequest,
  IListTranscriptionsResponse,
  ICompleteTranscriptionRequest,
  ICompleteTranscriptionResponse,
  IS3UploadCompleteAudioRequest,
  IS3UploadCompleteAudioResponse,
  IStopRecordingResponse,
} from "../../core/ports/HealthScribe";
import { IS3UploadProvider } from "../driven/providers/healthScribeS3UploadProvider";

export const HealthScribeControllerFactory = ({
  healthScribeService,
  healthScribeWebSocketProvider,
  healthScribeAudioProvider,
  s3UploadProvider,
}: {
  healthScribeService: IHealthScribeService;
  healthScribeWebSocketProvider: IHealthScribeWebSocketProvider;
  healthScribeAudioProvider: IHealthScribeAudioProvider;
  s3UploadProvider: IS3UploadProvider;
}): IHealthScribeController => {
  // Callback properties
  let _onTranscriptionMessage:
    | ((message: ITranscriptionMessage) => void)
    | null = null;
  let _onConnectionStatusChange:
    | ((status: "connected" | "disconnected" | "error") => void)
    | null = null;
  let _onAudioLevel: ((level: number) => void) | null = null;
  let _onAudioError: ((error: string) => void) | null = null;

  // Set up WebSocket callbacks
  healthScribeWebSocketProvider.onTranscription = (
    message: ITranscriptionMessage
  ) => {
    if (_onTranscriptionMessage) {
      _onTranscriptionMessage(message);
    }
  };

  healthScribeWebSocketProvider.onStatusChange = (
    status: "connected" | "disconnected" | "error"
  ) => {
    if (_onConnectionStatusChange) {
      _onConnectionStatusChange(status);
    }
  };

  healthScribeWebSocketProvider.onError = (error: string) => {
    if (_onAudioError) {
      _onAudioError(error);
    }
  };

  // Set up audio provider callbacks
  healthScribeAudioProvider.onAudioLevel = (level: number) => {
    if (_onAudioLevel) {
      _onAudioLevel(level);
    }
  };

  healthScribeAudioProvider.onError = (error: string) => {
    if (_onAudioError) {
      _onAudioError(error);
    }
  };

  const startRecording = async (
    request: IStartRecordingRequest
  ): Promise<IControllerPromiseResponse<IStartRecordingResponse>> => {
    try {
      console.log("[HealthScribe Controller] Starting recording:", request);

      // Start the session
      const sessionResult = await healthScribeService.startSession(request);
      if (!sessionResult.success) {
        return { success: false, error: sessionResult.error };
      }

      // Connect live transcription using WebSocket URL from response
      const connectResult = await healthScribeService.connectLiveTranscription(
        sessionResult.data!.session_id,
        sessionResult.data!.websocket_url
      );
      if (!connectResult.success) {
        return { success: false, error: connectResult.error };
      }

      // Start audio recording
      const audioResult = await healthScribeService.startAudioRecording();
      if (!audioResult.success) {
        return { success: false, error: audioResult.error };
      }

      console.log("[HealthScribe Controller] Recording started successfully");
      return { success: true, data: sessionResult.data };
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to start recording:",
        error
      );
      return { success: false, error: `Failed to start recording: ${error}` };
    }
  };

  const stopRecording = async (
    request: IStopRecordingRequest
  ): Promise<IControllerPromiseResponse<IStopRecordingResponse>> => {
    try {
      console.log("[HealthScribe Controller] Stopping recording:", request);

      // Stop the session (this will handle audio and S3 upload)
      const result = await healthScribeService.stopSession(request);
      if (!result.success) {
        return { success: false, error: result.error };
      }

      console.log("[HealthScribe Controller] Recording stopped successfully");
      return { success: true, data: result.data };
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to stop recording:",
        error
      );
      return { success: false, error: `Failed to stop recording: ${error}` };
    }
  };

  const connectLiveTranscription = async (
    sessionId: string,
    websocketUrl?: string
  ): Promise<IControllerPromiseResponse<void>> => {
    try {
      console.log(
        "[HealthScribe Controller] Connecting live transcription:",
        sessionId,
        websocketUrl
      );
      const result = await healthScribeService.connectLiveTranscription(
        sessionId,
        websocketUrl
      );
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to connect live transcription:",
        error
      );
      return {
        success: false,
        error: `Failed to connect live transcription: ${error}`,
      };
    }
  };

  const disconnectLiveTranscription = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      console.log("[HealthScribe Controller] Disconnecting live transcription");
      const result = await healthScribeService.disconnectLiveTranscription();
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to disconnect live transcription:",
        error
      );
      return {
        success: false,
        error: `Failed to disconnect live transcription: ${error}`,
      };
    }
  };

  const requestMicrophonePermission = async (): Promise<
    IControllerPromiseResponse<{ granted: boolean }>
  > => {
    try {
      console.log("[HealthScribe Controller] Requesting microphone permission");
      const result = await healthScribeService.requestMicrophonePermission();
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to request microphone permission:",
        error
      );
      return {
        success: false,
        error: `Failed to request microphone permission: ${error}`,
      };
    }
  };

  const startAudioRecording = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      console.log("[HealthScribe Controller] Starting audio recording");
      const result = await healthScribeService.startAudioRecording();
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to start audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to start audio recording: ${error}`,
      };
    }
  };

  const stopAudioRecording = async (): Promise<
    IControllerPromiseResponse<{
      audioBlob: Blob;
      duration: number;
    }>
  > => {
    try {
      console.log("[HealthScribe Controller] Stopping audio recording");
      const result = await healthScribeService.stopAudioRecording();
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to stop audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to stop audio recording: ${error}`,
      };
    }
  };

  const pauseAudioRecording = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      console.log("[HealthScribe Controller] Pausing audio recording");
      const result = await healthScribeService.pauseAudioRecording();
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to pause audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to pause audio recording: ${error}`,
      };
    }
  };

  const resumeAudioRecording = async (): Promise<
    IControllerPromiseResponse<void>
  > => {
    try {
      console.log("[HealthScribe Controller] Resuming audio recording");
      const result = await healthScribeService.resumeAudioRecording();
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to resume audio recording:",
        error
      );
      return {
        success: false,
        error: `Failed to resume audio recording: ${error}`,
      };
    }
  };

  const getCurrentSession = () => {
    return healthScribeService.getSession();
  };

  const populateSessionFromTranscription = (transcriptionData: {
    session_id: string;
    patient_id: string | number;
    clinic_id?: number;
    patient_name?: string;
    visit_type?: string;
    created_at?: string;
    transcription?: string;
    status?: "active" | "paused" | "completed" | "failed";
  }) => {
    console.log(
      "[HealthScribe Controller] Populating session from transcription:",
      transcriptionData
    );
    return healthScribeService.populateSessionFromTranscription(
      transcriptionData
    );
  };

  const getAudioLevel = (): number => {
    return healthScribeAudioProvider.getAudioLevel();
  };

  const uploadCompleteAudio = async (
    request: IS3UploadCompleteAudioRequest
  ): Promise<IControllerPromiseResponse<IS3UploadCompleteAudioResponse>> => {
    try {
      console.log("[HealthScribe Controller] Uploading complete audio:", {
        sessionId: request.session_id,
        clinicId: request.clinic_id,
        filename: request.filename,
        contentType: request.content_type,
        audioFileSize: request.audio_file.size,
      });

      const result = await healthScribeService.uploadCompleteAudio(request);
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to upload complete audio:",
        error
      );
      return {
        success: false,
        error: `Failed to upload complete audio: ${error}`,
      };
    }
  };

  const listTranscriptions = async (
    request: IListTranscriptionsRequest
  ): Promise<IControllerPromiseResponse<IListTranscriptionsResponse>> => {
    try {
      const result = await healthScribeService.listTranscriptions(request);
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to list transcriptions:",
        error
      );
      return {
        success: false,
        error: `Failed to list transcriptions: ${error}`,
      };
    }
  };

  const completeTranscriptionGenerateNarrative = async (
    request: ICompleteTranscriptionRequest
  ): Promise<IControllerPromiseResponse<ICompleteTranscriptionResponse>> => {
    try {
      console.log(
        "[HealthScribe Controller] Completing transcription generate narrative:",
        {
          transcriptionId: request.transcription_id,
          clinicId: request.clinic_id,
        }
      );
      const result =
        await healthScribeService.completeTranscriptionGenerateNarrative(
          request
        );
      return result;
    } catch (error) {
      console.error(
        "[HealthScribe Controller] Failed to complete transcription generate narrative:",
        error
      );
      return {
        success: false,
        error: `Failed to complete transcription generate narrative: ${error}`,
      };
    }
  };

  return {
    startRecording,
    stopRecording,
    connectLiveTranscription,
    disconnectLiveTranscription,
    requestMicrophonePermission,
    startAudioRecording,
    stopAudioRecording,
    pauseAudioRecording,
    resumeAudioRecording,
    getCurrentSession,
    populateSessionFromTranscription,
    get onTranscriptionMessage() {
      return _onTranscriptionMessage;
    },
    set onTranscriptionMessage(
      callback: ((message: ITranscriptionMessage) => void) | null
    ) {
      _onTranscriptionMessage = callback;
    },
    get onConnectionStatusChange() {
      return _onConnectionStatusChange;
    },
    set onConnectionStatusChange(
      callback:
        | ((status: "connected" | "disconnected" | "error") => void)
        | null
    ) {
      _onConnectionStatusChange = callback;
    },
    get onAudioLevel() {
      return _onAudioLevel;
    },
    set onAudioLevel(callback: ((level: number) => void) | null) {
      _onAudioLevel = callback;
    },
    get onAudioError() {
      return _onAudioError;
    },
    set onAudioError(callback: ((error: string) => void) | null) {
      _onAudioError = callback;
    },
    getAudioLevel,
    uploadCompleteAudio,
    listTranscriptions,
    completeTranscriptionGenerateNarrative,
  };
};
